import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  LogOut,
  Mail,
  Phone,
  Calendar,
  MessageSquare,
  Filter,
  Inbox,
  CheckCircle2,
  Clock,
  Trash2,
  RefreshCw,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { useToast } from '../hooks/use-toast';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-800 border-blue-300',
  contacted: 'bg-amber-100 text-amber-800 border-amber-300',
  completed: 'bg-green-100 text-green-800 border-green-300',
};

const STATUS_ICONS = {
  new: Inbox,
  contacted: Clock,
  completed: CheckCircle2,
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [quotes, setQuotes] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [user, setUser] = useState(null);
  const [deleteId, setDeleteId] = useState(null);

  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
  const getAuthHeader = () => ({
    Authorization: `Bearer ${localStorage.getItem('am_admin_token')}`,
  });

  const handleLogout = () => {
    localStorage.removeItem('am_admin_token');
    localStorage.removeItem('am_admin_user');
    navigate('/admin/login');
  };

  const fetchQuotes = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/quote-requests`, {
        headers: getAuthHeader(),
      });
      if (response.data.success) {
        setQuotes(response.data.data);
      }
    } catch (err) {
      if (err.response?.status === 401) {
        handleLogout();
      } else {
        toast({
          title: 'Error',
          description: 'Failed to load quote requests',
          variant: 'destructive',
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('am_admin_token');
    const storedUser = localStorage.getItem('am_admin_user');
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (storedUser) setUser(JSON.parse(storedUser));
    fetchQuotes();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateStatus = async (id, newStatus) => {
    try {
      await axios.patch(
        `${API}/quote-requests/${id}/status`,
        { status: newStatus },
        { headers: getAuthHeader() }
      );
      setQuotes((prev) =>
        prev.map((q) => (q.id === id ? { ...q, status: newStatus } : q))
      );
      toast({
        title: 'Status Updated',
        description: `Marked as ${newStatus}`,
      });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to update status',
        variant: 'destructive',
      });
    }
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      await axios.delete(`${API}/quote-requests/${deleteId}`, {
        headers: getAuthHeader(),
      });
      setQuotes((prev) => prev.filter((q) => q.id !== deleteId));
      toast({ title: 'Deleted', description: 'Quote request removed' });
    } catch (err) {
      toast({
        title: 'Error',
        description: 'Failed to delete',
        variant: 'destructive',
      });
    } finally {
      setDeleteId(null);
    }
  };

  const filteredQuotes =
    filter === 'all' ? quotes : quotes.filter((q) => q.status === filter);

  const stats = {
    total: quotes.length,
    new: quotes.filter((q) => q.status === 'new').length,
    contacted: quotes.filter((q) => q.status === 'contacted').length,
    completed: quotes.filter((q) => q.status === 'completed').length,
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  return (
    <div className="min-h-screen bg-amber-50">
      {/* Header */}
      <header className="bg-gradient-to-r from-amber-900 to-amber-800 text-amber-50 shadow-lg">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <img
                src="https://customer-assets.emergentagent.com/job_build-showcase-148/artifacts/4oqualfy_temp_image_B69CD62C-A65E-43CF-A04E-C52C20BE3294.WEBP"
                alt="AM Carpentry Logo"
                className="h-10 w-10 object-contain bg-white rounded-full p-1"
              />
              <div>
                <h1 className="text-xl font-bold">AM Carpentry Admin</h1>
                {user && (
                  <p className="text-xs text-amber-200">{user.email}</p>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={fetchQuotes}
                variant="outline"
                size="sm"
                className="bg-amber-700 border-amber-600 text-white hover:bg-amber-600"
                data-testid="admin-refresh-btn"
              >
                <RefreshCw className="w-4 h-4 mr-1" />
                Refresh
              </Button>
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="bg-amber-700 border-amber-600 text-white hover:bg-amber-600"
              >
                View Site
              </Button>
              <Button
                onClick={handleLogout}
                size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="admin-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <StatCard label="Total" value={stats.total} icon={MessageSquare} color="amber" />
          <StatCard label="New" value={stats.new} icon={Inbox} color="blue" />
          <StatCard label="Contacted" value={stats.contacted} icon={Clock} color="amber" />
          <StatCard label="Completed" value={stats.completed} icon={CheckCircle2} color="green" />
        </div>

        {/* Filter */}
        <div className="flex items-center gap-3 mb-6">
          <Filter className="w-5 h-5 text-amber-800" />
          <span className="text-amber-900 font-medium">Filter:</span>
          <Select value={filter} onValueChange={setFilter}>
            <SelectTrigger className="w-48 border-amber-300 bg-white" data-testid="admin-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Requests</SelectItem>
              <SelectItem value="new">New</SelectItem>
              <SelectItem value="contacted">Contacted</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
            </SelectContent>
          </Select>
          <span className="text-sm text-amber-700 ml-2">
            Showing {filteredQuotes.length} of {quotes.length}
          </span>
        </div>

        {/* Quote List */}
        {isLoading ? (
          <div className="text-center py-12 text-amber-800">Loading...</div>
        ) : filteredQuotes.length === 0 ? (
          <Card className="border-amber-200">
            <CardContent className="p-12 text-center">
              <Inbox className="w-16 h-16 text-amber-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-amber-950 mb-2">No quote requests</h3>
              <p className="text-amber-700">
                {filter === 'all'
                  ? "You haven't received any quote requests yet."
                  : `No ${filter} requests at the moment.`}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4" data-testid="admin-quotes-list">
            {filteredQuotes.map((quote) => {
              const StatusIcon = STATUS_ICONS[quote.status] || Inbox;
              return (
                <Card key={quote.id} className="border-2 border-amber-200 hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between flex-wrap gap-3 mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-amber-950">{quote.name}</h3>
                        <div className="flex items-center gap-1 text-sm text-amber-700 mt-1">
                          <Calendar className="w-4 h-4" />
                          {formatDate(quote.created_at)}
                        </div>
                      </div>
                      <Badge className={`${STATUS_COLORS[quote.status]} border px-3 py-1 flex items-center gap-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {quote.status.toUpperCase()}
                      </Badge>
                    </div>

                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <a
                        href={`mailto:${quote.email}`}
                        className="flex items-center gap-2 text-amber-800 hover:text-amber-600 transition-colors"
                      >
                        <Mail className="w-4 h-4 flex-shrink-0" />
                        <span className="break-all">{quote.email}</span>
                      </a>
                      {quote.phone && (
                        <a
                          href={`tel:${quote.phone}`}
                          className="flex items-center gap-2 text-amber-800 hover:text-amber-600 transition-colors"
                        >
                          <Phone className="w-4 h-4 flex-shrink-0" />
                          {quote.phone}
                        </a>
                      )}
                    </div>

                    <div className="bg-amber-50 border border-amber-100 rounded-md p-4 mb-4">
                      <p className="text-amber-900 whitespace-pre-wrap">{quote.message}</p>
                    </div>

                    <div className="flex items-center gap-2 flex-wrap">
                      <Select
                        value={quote.status}
                        onValueChange={(value) => updateStatus(quote.id, value)}
                      >
                        <SelectTrigger className="w-40 border-amber-300" data-testid={`status-select-${quote.id}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">New</SelectItem>
                          <SelectItem value="contacted">Contacted</SelectItem>
                          <SelectItem value="completed">Completed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`mailto:${quote.email}`)}
                        className="border-amber-700 text-amber-800 hover:bg-amber-100"
                      >
                        <Mail className="w-4 h-4 mr-1" />
                        Reply
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setDeleteId(quote.id)}
                        className="border-red-300 text-red-700 hover:bg-red-50"
                        data-testid={`delete-quote-${quote.id}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Delete
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(open) => !open && setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Quote Request?</AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete this quote request. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={confirmDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-quote"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

const StatCard = ({ label, value, icon: Icon, color }) => {
  const colorClasses = {
    amber: 'bg-amber-100 text-amber-900 border-amber-200',
    blue: 'bg-blue-50 text-blue-900 border-blue-200',
    green: 'bg-green-50 text-green-900 border-green-200',
  };
  return (
    <Card className={`border-2 ${colorClasses[color]}`}>
      <CardContent className="p-4 flex items-center gap-3">
        <Icon className="w-8 h-8 opacity-70" />
        <div>
          <div className="text-2xl font-bold">{value}</div>
          <div className="text-sm opacity-80">{label}</div>
        </div>
      </CardContent>
    </Card>
  );
};

export default AdminDashboard;
